import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCart } from './CartContext';
import { useStore } from './StoreContext';
import { insights } from '../services/algoliaInsights';
import { PRODUCT_INDEX } from '../services/algoliaClient';
import { findAlternative, UPSELL_CONFIG } from '../services/alternativeFinder';
import { buildUpsellCopy } from '../utils/upsellReason';
import { getEffectiveProductPrice } from '../utils/userProfile';

const UpsellContext = createContext(null);

const CONCURRENCY = 3;

/** @typedef {'idle'|'loading'|'ready'|'none'} UpsellLineStatus */

/**
 * @typedef {{
 *   status: UpsellLineStatus,
 *   fetchKey: string,
 *   payload?: {
 *     original: object,
 *     alternative: object,
 *     reasons: { key: string, value: unknown }[],
 *     priceDelta: number,
 *     priceDeltaPct: number,
 *     headline: string,
 *     body: string,
 *   },
 * }} LineUpsellEntry */

export const useUpsell = () => {
  const ctx = useContext(UpsellContext);
  if (!ctx) {
    throw new Error('useUpsell must be used within UpsellProvider');
  }
  return ctx;
};

function fetchKeyForLine(selectedStore, item) {
  const store = item?.selectedStore ?? selectedStore ?? '1';
  return `${store}_${String(item.objectID)}`;
}

export const UpsellProvider = ({ children }) => {
  const { cartItems, addToCart, removeFromCart } = useCart();
  const { selectedStore } = useStore();
  /** @type {React.MutableRefObject<Record<string, LineUpsellEntry>>} */
  const lineUpsellsRef = useRef({});
  const [lineUpsells, setLineUpsells] = useState(() => ({}));

  const [activeModalUpsell, setActiveModalUpsell] = useState(null);
  const activeModalUpsellRef = useRef(null);
  activeModalUpsellRef.current = activeModalUpsell;

  const teaserTrackedRef = useRef(new Set());

  useEffect(() => {
    lineUpsellsRef.current = lineUpsells;
  }, [lineUpsells]);

  useEffect(() => {
    if (cartItems.length === 0) {
      teaserTrackedRef.current.clear();
    }
  }, [cartItems.length]);

  useEffect(() => {
    const prev = lineUpsellsRef.current;
    const excludeIds = cartItems.map((i) => String(i.objectID));
    /** @type {Record<string, LineUpsellEntry>} */
    const next = {};
    /** @type {{ item: object, key: string }[]} */
    const toFetch = [];

    for (const item of cartItems) {
      const line = {
        ...item,
        selectedStore: item.selectedStore ?? selectedStore ?? '1',
      };
      const id = String(line.objectID);
      const key = fetchKeyForLine(selectedStore, line);
      const price = getEffectiveProductPrice(line);
      const cur = prev[id];

      if (price <= 0) {
        next[id] = { status: 'none', fetchKey: key };
      } else if (cur?.fetchKey === key && cur.status === 'ready') {
        next[id] = cur;
      } else if (cur?.fetchKey === key && cur.status === 'none') {
        next[id] = cur;
      } else {
        next[id] = { status: 'loading', fetchKey: key };
        toFetch.push({ item: line, key });
      }
    }

    setLineUpsells(next);

    if (toFetch.length === 0) return undefined;

    let cancelled = false;

    const runFetch = async ({ item, key }) => {
      const id = String(item.objectID);
      try {
        const result = await findAlternative(item, {
          excludeObjectIds: excludeIds,
          minUpPct: UPSELL_CONFIG.minUpPct,
          maxUpPct: UPSELL_CONFIG.maxUpPct,
          candidatePoolSize: UPSELL_CONFIG.candidatePoolSize,
        });
        if (cancelled) return;
        setLineUpsells((p) => {
          if (p[id]?.fetchKey !== key) return p;
          if (!result?.product) {
            return { ...p, [id]: { status: 'none', fetchKey: key } };
          }
          const { headline, body } = buildUpsellCopy({
            original: item,
            alternative: result.product,
            reasons: result.reasons,
            priceDeltaPct: result.priceDeltaPct,
          });
          const payload = {
            original: item,
            alternative: result.product,
            reasons: result.reasons,
            priceDelta: result.priceDelta,
            priceDeltaPct: result.priceDeltaPct,
            headline,
            body,
          };
          if (!teaserTrackedRef.current.has(id)) {
            teaserTrackedRef.current.add(id);
            const altPrice = getEffectiveProductPrice({
              ...result.product,
              selectedStore: item.selectedStore,
            });
            insights.convertedObjectIDs(
              'Upsell Shown',
              PRODUCT_INDEX,
              [result.product.objectID],
              altPrice
            );
          }
          return { ...p, [id]: { status: 'ready', fetchKey: key, payload } };
        });
      } catch (e) {
        console.warn('[UpsellContext] findAlternative', e);
        if (cancelled) return;
        setLineUpsells((p) =>
          p[id]?.fetchKey !== key ? p : { ...p, [id]: { status: 'none', fetchKey: key } }
        );
      }
    };

    const pool = async () => {
      for (let i = 0; i < toFetch.length; i += CONCURRENCY) {
        if (cancelled) return;
        const chunk = toFetch.slice(i, i + CONCURRENCY);
        await Promise.all(chunk.map(runFetch));
      }
    };

    void pool();

    return () => {
      cancelled = true;
    };
  }, [cartItems, selectedStore]);

  const dismissSilently = useCallback(() => {
    setActiveModalUpsell(null);
  }, []);

  const dismiss = useCallback(() => {
    const prev = activeModalUpsellRef.current;
    if (prev?.alternative?.objectID) {
      insights.clickedObjectIDs('Upsell Dismissed', PRODUCT_INDEX, [prev.alternative.objectID]);
    }
    setActiveModalUpsell(null);
  }, []);

  const openUpsellModal = useCallback(
    (objectID) => {
      const id = String(objectID);
      const row = lineUpsellsRef.current[id];
      if (row?.status !== 'ready' || !row.payload) return;
      const line = cartItems.find((c) => String(c.objectID) === id);
      if (!line) return;
      const lineQuantity = Math.max(1, Number(line.quantity) || 1);
      if (row.payload.alternative?.objectID) {
        insights.clickedObjectIDs('Upsell Modal Opened', PRODUCT_INDEX, [
          row.payload.alternative.objectID,
        ]);
      }
      setActiveModalUpsell({ ...row.payload, lineQuantity });
    },
    [cartItems]
  );

  const acceptReplace = useCallback(() => {
    const prev = activeModalUpsellRef.current;
    if (!prev?.original || !prev?.alternative) return;
    const alt = prev.alternative;
    const orig = prev.original;
    const qty = Math.max(1, Number(prev.lineQuantity) || 1);
    const store = orig.selectedStore;
    const altPrice = getEffectiveProductPrice({ ...alt, selectedStore: store });
    insights.convertedObjectIDs('Upsell Accepted - Replace', PRODUCT_INDEX, [alt.objectID], altPrice);
    removeFromCart(orig.objectID);
    addToCart({ ...alt, selectedStore: store }, qty, null);
    setActiveModalUpsell(null);
  }, [addToCart, removeFromCart]);

  const acceptAddBoth = useCallback(() => {
    const prev = activeModalUpsellRef.current;
    if (!prev?.original || !prev?.alternative) return;
    const alt = prev.alternative;
    const store = prev.original.selectedStore;
    const altPrice = getEffectiveProductPrice({ ...alt, selectedStore: store });
    insights.convertedObjectIDs('Upsell Accepted - AddBoth', PRODUCT_INDEX, [alt.objectID], altPrice);
    addToCart({ ...alt, selectedStore: store }, 1, null);
    setActiveModalUpsell(null);
  }, [addToCart]);

  const hasAnyReadyUpsell = useMemo(
    () => Object.values(lineUpsells).some((r) => r?.status === 'ready'),
    [lineUpsells]
  );

  const value = {
    lineUpsells,
    activeModalUpsell,
    hasAnyReadyUpsell,
    openUpsellModal,
    dismiss,
    dismissSilently,
    acceptReplace,
    acceptAddBoth,
  };

  return <UpsellContext.Provider value={value}>{children}</UpsellContext.Provider>;
};
